import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  createOpsContact,
  getOpsContact,
  getOpsContactHistory,
  searchOpsAccounts,
  updateOpsContact,
} from '../../../requests';
import { TOpsContactUpsert } from '../../../types';
import CommentThread from '../CommentThread';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pill,
} from '../ui';

// The values the org actually uses (DM_Category__c, counted in the mirror),
// most common first.
const COMMON_ROLES = [
  'Student',
  'Decision Maker',
  'Coordinator',
  'Influencer',
  'Human Resources',
  'Recruiting & Talent Acquisition',
  'Human Resources Executive',
  'Learning & Development',
  'Compensation & Benefits',
  'Finance',
  'Instructor',
];

const OpsContactEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobilePhone: '',
    title: '',
    roles: '',
    left: false,
  });
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountQuery, setAccountQuery] = useState('');

  const { data: history } = useQuery({
    queryKey: ['opsContactHistory', id],
    queryFn: () => getOpsContactHistory(id!),
    enabled: !isNew,
  });
  const { data: existing, isLoading } = useQuery({
    queryKey: ['opsContact', id],
    queryFn: () => getOpsContact(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      firstName: existing.firstName ?? '',
      lastName: existing.lastName ?? '',
      email: existing.email ?? '',
      phone: existing.phone ?? '',
      mobilePhone: existing.mobilePhone ?? '',
      title: existing.title ?? '',
      roles: existing.roles ?? '',
      left: existing.left,
    });
    setAccountId(existing.accountId ?? '');
    setAccountName(existing.accountName ?? '');
  }, [existing]);

  const { data: accountHits = [] } = useQuery({
    queryKey: ['opsAccounts', accountQuery],
    queryFn: () => searchOpsAccounts(accountQuery),
    enabled: accountQuery.trim().length >= 2,
  });

  const save = useMutation({
    mutationFn: (dto: TOpsContactUpsert) =>
      isNew ? createOpsContact(dto) : updateOpsContact(id!, dto),
    onSuccess: () => {
      showToast(isNew ? 'Contact created' : 'Contact saved', 'success');
      navigate(PATH.opsContacts);
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      showToast('First and last name are required', 'error');
      return;
    }
    save.mutate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      mobilePhone: form.mobilePhone.trim() || undefined,
      title: form.title.trim() || undefined,
      accountId: accountId || undefined,
      roles: form.roles.trim() || undefined,
      left: form.left,
    });
  };

  const set = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'New contact' : 'Edit contact'}
        back={{ to: PATH.opsContacts, label: 'Back to contacts' }}
      />
      <PageBody>
        <form onSubmit={submit} className="max-w-3xl">
          {!isNew && existing && !existing.localOrigin && (
            <div className="mb-4 rounded-lg border border-yellow bg-yellow-500 px-4 py-3 text-sm text-yellow-900">
              This contact was born in Salesforce. Until cutover, a data refresh
              can overwrite these fields with Salesforce's version — make the
              change there too, or expect it to revert.
            </div>
          )}
          <Card>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              <div>
                <label className={labelClass}>First name</label>
                <input
                  className={`${inputClass} w-full`}
                  value={form.firstName}
                  onChange={(e) => set('firstName')(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Last name</label>
                <input
                  className={`${inputClass} w-full`}
                  value={form.lastName}
                  onChange={(e) => set('lastName')(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  className={`${inputClass} w-full`}
                  value={form.email}
                  onChange={(e) => set('email')(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Title</label>
                <input
                  className={`${inputClass} w-full`}
                  value={form.title}
                  onChange={(e) => set('title')(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  className={`${inputClass} w-full`}
                  value={form.phone}
                  onChange={(e) => set('phone')(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Mobile</label>
                <input
                  className={`${inputClass} w-full`}
                  value={form.mobilePhone}
                  onChange={(e) => set('mobilePhone')(e.target.value)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass}>Account</label>
                {accountId ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="rounded-md bg-blue-100 px-2.5 py-1 font-medium text-blue-primary">
                      {accountName}
                    </span>
                    <button
                      type="button"
                      className={`${btnGhost} text-xs`}
                      onClick={() => {
                        setAccountId('');
                        setAccountName('');
                        setAccountQuery('');
                      }}
                    >
                      change
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      className={`${inputClass} w-full`}
                      placeholder="Type at least 2 characters to search accounts…"
                      value={accountQuery}
                      onChange={(e) => setAccountQuery(e.target.value)}
                    />
                    {accountHits.length > 0 && (
                      <div className="mt-1 overflow-hidden rounded-md border border-white-two bg-white shadow-light">
                        {accountHits.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/50"
                            onClick={() => {
                              setAccountId(a.id);
                              setAccountName(a.name);
                            }}
                          >
                            {a.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className={labelClass}>Role</label>
                <select
                  className={`${inputClass} w-full`}
                  value={
                    COMMON_ROLES.includes(form.roles) || form.roles === ''
                      ? form.roles
                      : '__other'
                  }
                  onChange={(e) =>
                    set('roles')(
                      e.target.value === '__other' ? ' ' : e.target.value,
                    )
                  }
                >
                  <option value="">— none —</option>
                  {COMMON_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  <option value="__other">Other…</option>
                </select>
                {form.roles !== '' && !COMMON_ROLES.includes(form.roles) && (
                  <input
                    className={`${inputClass} mt-1 w-full`}
                    autoFocus
                    placeholder="Type the role"
                    value={form.roles.trim() === '' ? '' : form.roles}
                    onChange={(e) => set('roles')(e.target.value || ' ')}
                  />
                )}
              </div>
              <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-five">
                <input
                  type="checkbox"
                  className="ops-check"
                  checked={form.left}
                  onChange={(e) => set('left')(e.target.checked)}
                />
                No longer at company
              </label>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-white-three px-5 py-3">
              <button
                type="button"
                className={btnSecondary}
                onClick={() => navigate(PATH.opsContacts)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={save.isPending}
                className={btnPrimary}
              >
                {save.isPending
                  ? 'Saving…'
                  : isNew
                  ? 'Create contact'
                  : 'Save changes'}
              </button>
            </div>
          </Card>
        </form>
        {!isNew && history && (
          <div className="mt-6 grid max-w-5xl gap-4 lg:grid-cols-2">
            <Card
              title={`Registrations · ${history.registrations.length}`}
              right={
                history.registrations.length > 0 ? (
                  <Link
                    className="text-xs text-blue-primary hover:underline"
                    to={`${PATH.opsRegistrations}?contactId=${history.contactId}`}
                  >
                    open in the list
                  </Link>
                ) : undefined
              }
            >
              {history.registrations.length === 0 ? (
                <div className="px-5 py-4 text-sm text-gray-bold">
                  No registrations on record for this person.
                </div>
              ) : (
                <ul className="divide-y divide-white-three text-sm">
                  {history.registrations.slice(0, 50).map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-3 px-5 py-2"
                    >
                      <span className="min-w-0">
                        {r.classId ? (
                          <Link
                            className="font-medium text-gray-four hover:text-blue-primary hover:underline"
                            to={PATH.opsClassEdit.replace(':id', r.classId)}
                          >
                            {r.courseName ?? '(unnamed class)'}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-four">
                            {r.courseName ?? '(unnamed class)'}
                          </span>
                        )}
                        <span className="ml-2 text-xs text-gray-bold">
                          {r.courseStart?.slice(0, 10) ?? '—'}
                          {r.accountName ? ` · ${r.accountName}` : ''}
                          {r.category ? ` · ${r.category}` : ''}
                        </span>
                      </span>
                      <Pill value={r.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title={`Evaluations · ${history.evaluations.length}`}>
              {history.evaluations.length === 0 ? (
                <div className="px-5 py-4 text-sm text-gray-bold">
                  No evaluation from this person on record.
                </div>
              ) : (
                <ul className="divide-y divide-white-three text-sm">
                  {history.evaluations.slice(0, 50).map((e) => (
                    <li key={e.id} className="px-5 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-gray-four">
                          {e.courseName ?? '(course)'}
                        </span>
                        <span className="text-xs text-gray-bold">
                          {e.courseDate?.slice(0, 10) ??
                            e.evalDate?.slice(0, 10) ??
                            '—'}
                          {e.rating != null ? ` · ${e.rating}/5` : ''}
                          {e.recommend ? ` · recommends: ${e.recommend}` : ''}
                          {e.linkedBy === 'email' ? ' · matched by email' : ''}
                        </span>
                      </div>
                      {e.feedback && (
                        <div className="mt-0.5 text-xs text-gray-five">
                          {e.feedback}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
        {!isNew && id && (
          <div className="mt-5">
            <CommentThread recordId={id} recordObject="contact" />
          </div>
        )}
      </PageBody>
    </>
  );
};

export default OpsContactEditor;
